import { Controller } from "@hotwired/stimulus"

// Manage-rules modal controller: loads rules via AJAX, toggles enable/disable,
// opens the edit modal via `portfolio-alert:open-edit`, installs starter rules,
// and gates delete behind a styled confirmation modal (never confirm()).

export default class extends Controller {
  static targets = [
    "list", "empty", "loading", "summary", "rowTemplate",
    "errors", "starterErrors", "deleteMessage"
  ]
  static values = {
    indexUrl: String,
    updateUrl: String,
    installUrl: String,
    starters: Object
  }

  connect() {
    this._onOpen = this._onOpen.bind(this)
    this._onRefresh = this._onRefresh.bind(this)
    document.addEventListener("portfolio-alert-manage:open", this._onOpen)
    document.addEventListener("portfolio-alert-manage:refresh", this._onRefresh)
    this._pendingDeleteId = null
  }

  disconnect() {
    document.removeEventListener("portfolio-alert-manage:open", this._onOpen)
    document.removeEventListener("portfolio-alert-manage:refresh", this._onRefresh)
  }

  _onOpen() {
    document.dispatchEvent(new CustomEvent("modal:open-by-id", { detail: { id: "portfolio-alert-manage" } }))
    this._load()
  }

  _onRefresh() {
    this._load()
  }

  // ── Load + render ────────────────────────────────────────────

  async _load() {
    this.loadingTarget.classList.remove("hidden")
    this.listTarget.classList.add("hidden")
    this.emptyTarget.classList.add("hidden")
    this._clearErrors()
    try {
      const response = await fetch(this.indexUrlValue, {
        headers: { Accept: "application/json" }
      })
      const data = await response.json()
      if (!response.ok) {
        this._showErrors((data.errors || ["Failed to load rules"]))
        return
      }
      this._render(data.rules || [])
    } catch (err) {
      this._showErrors([err.message || "Network error"])
    } finally {
      this.loadingTarget.classList.add("hidden")
    }
  }

  _render(rules) {
    this.listTarget.innerHTML = ""
    if (!rules.length) {
      this.summaryTarget.textContent = "No rules yet"
      this.emptyTarget.classList.remove("hidden")
      return
    }
    const enabledCount = rules.filter(r => r.enabled).length
    this.summaryTarget.textContent = `${rules.length} rule${rules.length === 1 ? "" : "s"} · ${enabledCount} active`

    rules.forEach(rule => this.listTarget.appendChild(this._buildRow(rule)))
    this.listTarget.classList.remove("hidden")
  }

  _buildRow(rule) {
    const node = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
    node.dataset.id = rule.id
    node.dataset.rule = JSON.stringify(rule)

    node.querySelector("[data-role='name']").textContent = rule.name
    node.querySelector("[data-role='frequency']").textContent = humanizeFrequency(rule.frequency)
    node.querySelector("[data-role='sentence']").textContent = sentenceFor(rule)

    const toggle = node.querySelector("[data-role='toggle']")
    toggle.checked = !!rule.enabled
    toggle.addEventListener("change", (event) => this._toggleEnabled(rule.id, event.target.checked, node))

    const disabledBadge = node.querySelector("[data-role='disabled-badge']")
    if (!rule.enabled) disabledBadge.classList.remove("hidden")

    const meta = node.querySelector("[data-role='meta']")
    meta.textContent = metaLine(rule)

    return node
  }

  // ── Actions ──────────────────────────────────────────────────

  openNew() {
    this._closeModal("portfolio-alert-manage-modal")
    document.dispatchEvent(new CustomEvent("portfolio-alert-form:open", { detail: { rule: null } }))
    document.dispatchEvent(new CustomEvent("modal:open-by-id", { detail: { id: "portfolio-alert-form" } }))
  }

  edit(event) {
    const row = event.currentTarget.closest("li[data-id]")
    if (!row) return
    const rule = JSON.parse(row.dataset.rule || "null")
    if (!rule) return
    this._closeModal("portfolio-alert-manage-modal")
    document.dispatchEvent(new CustomEvent("portfolio-alert-form:open", { detail: { rule } }))
    document.dispatchEvent(new CustomEvent("modal:open-by-id", { detail: { id: "portfolio-alert-form" } }))
  }

  _closeModal(id) {
    const el = document.getElementById(id)
    if (!el) return
    el.classList.add("hidden")
    document.body.style.overflow = ""
  }

  async _toggleEnabled(id, enabled, node) {
    try {
      const response = await fetch(`${this.updateUrlValue}/${id}`, {
        method: "PATCH",
        headers: jsonHeaders(),
        body: JSON.stringify({ enabled })
      })
      const data = await response.json()
      if (!response.ok || !data.success) {
        this._showToast((data.errors || ["Update failed"]).join(", "), "alert")
        return
      }
      const rule = data.rule
      node.dataset.rule = JSON.stringify(rule)
      const disabledBadge = node.querySelector("[data-role='disabled-badge']")
      disabledBadge.classList.toggle("hidden", rule.enabled)
      this._updateSummary()
    } catch (err) {
      this._showToast(err.message || "Network error", "alert")
    }
  }

  _updateSummary() {
    const rows = this.listTarget.querySelectorAll("li[data-id]")
    let enabled = 0
    rows.forEach(r => { if (JSON.parse(r.dataset.rule || "{}").enabled) enabled++ })
    this.summaryTarget.textContent = `${rows.length} rule${rows.length === 1 ? "" : "s"} · ${enabled} active`
  }

  confirmDelete(event) {
    const row = event.currentTarget.closest("li[data-id]")
    if (!row) return
    const rule = JSON.parse(row.dataset.rule || "null")
    this._pendingDeleteId = rule?.id
    this.deleteMessageTarget.textContent = `"${rule.name}" will stop firing. This can't be undone.`
    document.dispatchEvent(new CustomEvent("modal:open-by-id", { detail: { id: "portfolio-alert-delete" } }))
  }

  async doDelete() {
    const id = this._pendingDeleteId
    if (!id) return
    try {
      const response = await fetch(`${this.updateUrlValue}/${id}`, {
        method: "DELETE",
        headers: jsonHeaders()
      })
      const data = await response.json()
      if (!response.ok || !data.success) {
        this._showToast((data.errors || ["Delete failed"]).join(", "), "alert")
        return
      }
      this._showToast("Alert rule deleted", "notice")
      this._pendingDeleteId = null
      this._closeModal("portfolio-alert-delete-modal")
      this._load()
    } catch (err) {
      this._showToast(err.message || "Network error", "alert")
    }
  }

  openStarters() {
    document.dispatchEvent(new CustomEvent("modal:open-by-id", { detail: { id: "portfolio-alert-starters" } }))
  }

  async installStarter(event) {
    const slug = event.currentTarget.dataset.slug
    if (!slug) return
    event.currentTarget.disabled = true
    try {
      const response = await fetch(this.installUrlValue, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ slug })
      })
      const data = await response.json()
      if (!response.ok || !data.success) {
        this._showStarterErrors(data.errors || ["Install failed"])
        return
      }
      this._showToast(`Installed "${data.rule.name}"`, "notice")
      this._closeModal("portfolio-alert-starters-modal")
      this._load()
    } catch (err) {
      this._showStarterErrors([err.message || "Network error"])
    } finally {
      event.currentTarget.disabled = false
    }
  }

  // ── Helpers ──────────────────────────────────────────────────

  _clearErrors() {
    this.errorsTarget.classList.add("hidden")
    this.errorsTarget.textContent = ""
  }

  _showErrors(messages) {
    this.errorsTarget.classList.remove("hidden")
    this.errorsTarget.textContent = messages.join(" · ")
  }

  _showStarterErrors(messages) {
    if (!this.hasStarterErrorsTarget) return
    this.starterErrorsTarget.classList.remove("hidden")
    this.starterErrorsTarget.textContent = messages.join(" · ")
  }

  _showToast(message, type = "notice") {
    const toast = document.createElement("div")
    toast.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "5000")
    toast.textContent = message
    document.body.prepend(toast)
  }
}

function jsonHeaders() {
  return {
    "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
    Accept: "application/json",
    "Content-Type": "application/json"
  }
}

function humanizeFrequency(freq) {
  switch (freq) {
    case "immediate": return "Immediate"
    case "daily_digest": return "Daily"
    case "weekly_digest": return "Weekly"
    default: return freq || ""
  }
}

const METRIC_LABELS = {
  budget_variance_pct: "Budget variance",
  budget_variance_amount: "Budget variance",
  schedule_days_behind: "Schedule delay",
  overdue_items_count: "Overdue items",
  rag_status_changed_to: "RAG status",
  project_count_red: "Projects in red"
}

const OP_LABELS = {
  gt: "exceeds", gte: "is at least",
  lt: "is below", lte: "is at most",
  eq: "equals"
}

function sentenceFor(rule) {
  const c = rule.condition || {}
  const metric = METRIC_LABELS[c.metric] || c.metric || "?"
  const scope = scopeLabel(c.scope)
  if (c.metric === "rag_status_changed_to") {
    return `${metric} changed to ${c.value} on ${scope}`
  }
  const op = OP_LABELS[c.op] || c.op
  const unit = c.metric === "budget_variance_pct" ? "%" : c.metric === "schedule_days_behind" ? " days" : ""
  const prefix = c.metric === "budget_variance_amount" ? "$" : ""
  return `${metric} ${op} ${prefix}${c.value}${unit} on ${scope}`
}

function scopeLabel(scope) {
  if (!scope) return "all projects"
  switch (scope.type) {
    case "all": return "all projects"
    case "portfolio": return "portfolio"
    case "program": return "program"
    case "phase": return `${scope.value} phase`
    case "project_ids": return `${Array.isArray(scope.value) ? scope.value.length : 0} selected projects`
    default: return "all projects"
  }
}

function metaLine(rule) {
  const parts = []
  parts.push(`${rule.email_recipients.length} recipient${rule.email_recipients.length === 1 ? "" : "s"}`)
  if (rule.last_fired_at) {
    parts.push(`Fired ${new Date(rule.last_fired_at).toLocaleDateString()} · ${rule.fire_count} total`)
  } else {
    parts.push("Never fired")
  }
  return parts.join(" · ")
}
